/**
 * Pure helpers for the "record opening balance" flow (#24315).
 *
 * Both the Leave → Balances tab and the Employee → Payroll tab open the same
 * `LeaveAdjustDrawer` for an employee × leave-type pair that has NO row on
 * the wire yet (a brand-new client where no pay run has run, so no balance
 * has ever accrued). The drawer accepts a `LeaveAdjustTarget` — an
 * `EntityLeaveBalanceRow` extended with optional display fields — so both
 * surfaces synthesise a zero-valued row with an empty `id` (the backend's
 * `payroll.adjustLeaveBalance` upserts the row anyway and does not read the
 * `id` from the caller) plus the human-readable employee name and leave-type
 * label the drawer description binds to.
 *
 * This module also hosts the picker's and screen's status/validation/label
 * derivations so those pure branches can be unit-tested React-free —
 * `leave_opening_balance_picker.tsx` keeps JSX + event wiring only.
 */
import { leaveTypeLabelKey } from '../../data/wagepro_store_leave_balances';
import type { EntityLeaveBalanceRow } from '../../data/use_entity_leave_balances';
import type { LeaveAdjustTarget } from './leave_adjust_drawer';
import type { LeaveEmployeeLookup } from '../../data/wagepro_store_leave';

/**
 * Coarse status the opening-balance picker consumes for its employee-select
 * branch (skeleton / error alert / real select). `no_entity` and
 * `entitlement_required` are treated as errors here — the balances tab
 * already gates its own render on the balance-hook's state machine, so
 * either state is unreachable in the picker's own render path.
 */
export type EmployeesStatus = 'loading' | 'error' | 'ready';

/**
 * Coarse status the opening-balance picker consumes for its leave-type
 * select branch. The picker fires `useLeaveTypes()` internally, so
 * `no_entity` (entity context resolving) is coalesced into `loading` here
 * — otherwise the operator would see an "error" the moment they open the
 * drawer against an entity that is still resolving.
 */
export type LeaveTypesStatus = 'loading' | 'error' | 'ready';

/**
 * Maps the leave-requests state machine (`useLeaveRequests`'s
 * `LeaveState.status`) to the picker's coarser {@link EmployeesStatus}.
 * `no_entity` / `entitlement_required` / `error` all resolve to `'error'`
 * here — the tab already gates its own balances render on the balance-hook's
 * state, so the picker's employee-select branch only cares whether the
 * threaded employee list is loading, errored, or genuinely ready.
 */
export function resolveEmployeesStatus(
  status: 'loading' | 'no_entity' | 'entitlement_required' | 'error' | 'ready',
): EmployeesStatus {
  if (status === 'ready') return 'ready';
  if (status === 'loading') return 'loading';
  return 'error';
}

/**
 * Maps the leave-types state machine (`useLeaveTypes`'s
 * `LeaveTypesState.status`) to the picker's coarser {@link LeaveTypesStatus}.
 * `no_entity` is coalesced into `'loading'` because the entity context is
 * still resolving — showing "error" the instant the drawer opens against an
 * unresolved entity would be misleading. `entitlement_required` and `error`
 * both resolve to `'error'`.
 */
export function resolveLeaveTypesStatus(
  status: 'loading' | 'no_entity' | 'entitlement_required' | 'error' | 'ready',
): LeaveTypesStatus {
  if (status === 'ready') return 'ready';
  if (status === 'loading' || status === 'no_entity') return 'loading';
  return 'error';
}

/**
 * `true` when the employee-select branch is not ready to accept a
 * selection — either the fetch has not resolved (`loading` / `error`) or
 * it resolved to zero configured employees. Callers gate the picker's
 * Continue button on this.
 */
export function isEmployeesGated(
  status: EmployeesStatus,
  options_count: number,
): boolean {
  return status !== 'ready' || options_count === 0;
}

/**
 * `true` when the leave-type-select branch is not ready to accept a
 * selection — either the fetch has not resolved (`loading` / `error`) or it
 * resolved to zero balance-tracked types configured.
 */
export function isLeaveTypesGated(
  status: LeaveTypesStatus,
  options_count: number,
): boolean {
  return status !== 'ready' || options_count === 0;
}

/**
 * Full "picker can continue" predicate: both select branches must be ready
 * with at least one option, AND the operator must have chosen an employee
 * and a leave type. Field-level errors are still rendered by the picker's
 * event handler; this predicate is what would gate the primary action if
 * the picker never rendered helper text.
 */
export function canContinueOpeningBalance(input: {
  employee_id: string;
  leave_type: string;
  employees_status: EmployeesStatus;
  employees_options_count: number;
  leave_types_status: LeaveTypesStatus;
  leave_types_options_count: number;
}): boolean {
  if (isEmployeesGated(input.employees_status, input.employees_options_count)) {
    return false;
  }
  if (
    isLeaveTypesGated(input.leave_types_status, input.leave_types_options_count)
  ) {
    return false;
  }
  return input.employee_id !== '' && input.leave_type !== '';
}

/**
 * Resolves a leave-type code to a human-readable label:
 *
 *   1. Prefer the entity-configured `name` from the `leave_types` list
 *      (picks up a jurisdiction-localised rename of a system type).
 *   2. Else, fall back to the canonical i18n key for a balance-tracked code
 *      (annual/sick/casual).
 *   3. Else, fall back to the raw code so a jurisdiction-added custom type
 *      still renders something rather than a blank cell.
 *
 * `translate` is passed in so this function stays React-free and testable.
 */
export function resolveLeaveTypeLabel(input: {
  code: string;
  leave_types: readonly { code: string; name: string }[];
  translate: (key: string) => string;
}): string {
  const match = input.leave_types.find((lt) => lt.code === input.code);
  if (match) return match.name;
  const key = leaveTypeLabelKey(input.code);
  return key ? input.translate(key) : input.code;
}

/**
 * Resolves an employee id to a display name, falling back to the raw id
 * when the lookup misses (mid-fetch or stale row). Wrapper around
 * {@link employeeNameById} for callers that want the fallback baked in.
 */
export function resolveEmployeeDisplayName(input: {
  id: string;
  employees: readonly LeaveEmployeeLookup[];
}): string {
  return employeeNameById(input.employees, input.id) ?? input.id;
}

export function buildOpeningBalanceTarget(input: {
  employee_id: string;
  entity_id: string;
  leave_type: string;
  employee_name?: string;
  leave_type_label?: string;
}): LeaveAdjustTarget {
  const row: EntityLeaveBalanceRow = {
    id: '',
    employee_id: input.employee_id,
    entity_id: input.entity_id,
    leave_type: input.leave_type,
    balance_days_scaled: 0,
    total_days_scaled: 0,
    taken_days_scaled: 0,
    booked_days_scaled: 0,
    pending_days_scaled: 0,
    available_days_scaled: 0,
    // A synthetic zero target has no leave year of its own; the adjust
    // drawer never reads these, they only satisfy the row shape.
    leave_year_start: '',
    leave_year_end: '',
    balance_days: 0,
    total_days: 0,
    taken_days: 0,
    booked_days: 0,
    pending_days: 0,
    available_days: 0,
  };
  return {
    ...row,
    ...(input.employee_name !== undefined
      ? { employee_name: input.employee_name }
      : {}),
    ...(input.leave_type_label !== undefined
      ? { leave_type_label: input.leave_type_label }
      : {}),
  };
}

/**
 * Looks up an employee's display name by id. Returns `null` when the id
 * is not in the list — callers fall back to rendering the raw id so a
 * mid-fetch or stale row still renders something rather than nothing.
 */
export function employeeNameById(
  employees: readonly LeaveEmployeeLookup[],
  employee_id: string,
): string | null {
  const match = employees.find((emp) => emp.id === employee_id);
  return match ? match.full_name : null;
}
